import React from 'react';
import { Dialogue } from '../../types';
import { 
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  UserGroupIcon,
  PlayIcon,
  StarIcon,
  SpeakerWaveIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface DialogueCardProps {
  dialogue: Dialogue;
  onClick: () => void;
}

const DialogueCard: React.FC<DialogueCardProps> = ({ dialogue, onClick }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getParticipantNames = () => {
    if (!dialogue.participants || dialogue.participants.length === 0) {
      return 'No participants';
    }
    return dialogue.participants.map(p => p.name).join(', ');
  };

  const getAverageRating = () => {
    if (!dialogue.ratings || dialogue.ratings.length === 0) {
      return null;
    }
    const sum = dialogue.ratings.reduce((acc, rating) => acc + rating.overallScore, 0);
    return (sum / dialogue.ratings.length).toFixed(1);
  };

  const getLatestRating = () => {
    if (!dialogue.ratings || dialogue.ratings.length === 0) {
      return null;
    }
    return dialogue.ratings.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
  };

  const latestRating = getLatestRating();
  const averageRating = getAverageRating();

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
            {dialogue.title}
          </h3>
          {dialogue.case && (
            <p className="text-sm text-gray-600 mt-1">
              Case: {dialogue.case.title}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {averageRating && (
            <div className="flex items-center text-yellow-500">
              <StarIcon className="h-4 w-4" />
              <span className="text-sm font-medium ml-1">{averageRating}</span>
            </div>
          )}
          <EyeIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
        </div>
      </div>

      {/* Participants */}
      <div className="flex items-center text-sm text-gray-600 mb-3">
        <UserGroupIcon className="h-4 w-4 mr-2" />
        <span className="truncate">{getParticipantNames()}</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {dialogue.turns?.length || 0}
          </div>
          <div className="text-xs text-gray-600">Turns</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {dialogue.audioFiles?.length || 0}
          </div>
          <div className="text-xs text-gray-600">Audio</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-gray-900">
            {dialogue.ratings?.length || 0}
          </div>
          <div className="text-xs text-gray-600">Ratings</div>
        </div>
      </div>

      {/* Latest Rating */}
      {latestRating && (
        <div className="bg-gray-50 rounded-md p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <StarIcon className="h-4 w-4 text-yellow-500 mr-1" />
              <span className="text-sm font-medium text-gray-900">
                Latest Rating: {latestRating.overallScore}/5
              </span>
            </div>
            <span className="text-xs text-gray-500">
              {formatDate(latestRating.createdAt)}
            </span>
          </div>
          {latestRating.overallSummary && (
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              {latestRating.overallSummary}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center text-sm text-gray-500">
          <CalendarIcon className="h-4 w-4 mr-1" />
          <span>{formatDate(dialogue.createdAt)}</span>
        </div>
        
        <div className="flex items-center space-x-2">
          {dialogue.audioFiles && dialogue.audioFiles.length > 0 && (
            <div className="flex items-center text-blue-600">
              <SpeakerWaveIcon className="h-4 w-4" />
            </div>
          )}
          <div className="flex items-center text-gray-400 group-hover:text-blue-500 transition-colors">
            <PlayIcon className="h-4 w-4" />
          </div>
        </div>
      </div>

      {/* Setting */}
      {dialogue.setting && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            <span className="font-medium">Setting:</span> {dialogue.setting}
          </p>
        </div>
      )}
    </div>
  );
};

export default DialogueCard;
